import { useMemo, useRef, useState } from 'react';
import type { ComboboxOption } from '@standardsdigital/ui-components/Combobox';
import FormCard from 'components/global/FormCard/FormCard';
import FormErrorSummary from 'components/global/FormErrorSummary/FormErrorSummary';
import { useTranslation } from 'react-i18next';
import type { FieldPath, FieldPathValue, SubmitHandler } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateServiceLevelDefinitionMutation,
  useListServiceLevelDefinitionClassifiersQuery,
  useUpdateServiceLevelDefinitionMutation,
  useUpdateServiceLevelDefinitionNsbSetupsMutation,
} from 'redux/services/generate/configurations/serviceLevelDefinitions';
import type {
  AgreementLicenseType,
  CreateServiceLevelDefinitionRequest,
  DocumentLanguage,
  DocumentService,
  PreviousVersion,
  UpdateServiceLevelDefinitionRequestBody,
} from 'redux/services/generate/configurations/serviceLevelDefinitions';
import {
  CustomCollectionSubscriptionType,
  defaultServiceLevelFormValues,
  getFormSchema,
  getSelectedOptions,
  toBitEnumValue,
  toComboboxOptions,
  toNoYesEnum,
  type ServiceLevelFormValues,
} from '../../serviceLevelOptions';
import AllowedNsbsSelect from './FormInputs/AllowedNsbsSelect';
import BooleanCheckboxes from './FormInputs/BooleanCheckboxes';
import ConfirmationDialog from './FormInputs/ConfirmationDialog';
import LanguagesSelect from './FormInputs/LanguagesSelect';
import NameInput from './FormInputs/NameInput';
import PreviousVersionsRadio from './FormInputs/PreviousVersionsRadio';
import PriceFactorInput from './FormInputs/PriceFactorInput';
import PriceFactorModeRadio from './FormInputs/PriceFactorModeRadio';
import ServicesSelect from './FormInputs/ServicesSelect';
import SubscriptionTypeSelect from './FormInputs/SubscriptionTypeSelect';
import UpdateExistingSubscriptionsRadio from './FormInputs/UpdateExistingSubscriptionsRadio';
import Spinner from '@/components/global/Spinner/Spinner';
import { useToast } from '@/util/hooks';

interface ServiceLevelFormProps {
  formData: ServiceLevelFormValues;
  onClose: () => void;
  editingServiceLevelId?: number;
}

function ServiceLevelForm({
  formData,
  onClose,
  editingServiceLevelId,
}: Readonly<ServiceLevelFormProps>) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: classifiers, isFetching: isFetchingClassifiers } =
    useListServiceLevelDefinitionClassifiersQuery();
  const [createServiceLevelDefinition, createState] =
    useCreateServiceLevelDefinitionMutation();
  const [updateServiceLevelDefinition, updateState] =
    useUpdateServiceLevelDefinitionMutation();
  const [updateNsbSetups, updateNsbSetupsState] =
    useUpdateServiceLevelDefinitionNsbSetupsMutation();
  const isSaving =
    createState.isLoading || updateState.isLoading || updateNsbSetupsState.isLoading;
  const schema = getFormSchema();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    control,
    setValue,
    reset,
  } = useForm<ServiceLevelFormValues, unknown, ServiceLevelFormValues>({
    resolver: yupResolver(schema),
    defaultValues: formData,
  });
  const values = useWatch({
    control,
    defaultValue: formData,
  }) as ServiceLevelFormValues;
  const formRef = useRef<HTMLFormElement>(null);
  const isEditMode = editingServiceLevelId !== undefined;
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const announcementTexts = useMemo(
    () => ({
      selected: t('general.comboboxAnnouncements.selected'),
      deselected: t('general.comboboxAnnouncements.deselected'),
    }),
    [t],
  );
  const documentServiceComboboxOptions = useMemo(
    () => toComboboxOptions(classifiers?.documentServices),
    [classifiers?.documentServices],
  );
  const languageComboboxOptions = useMemo(
    () => toComboboxOptions(classifiers?.languages),
    [classifiers?.languages],
  );
  const previousVersionComboboxOptions: ComboboxOption[] = useMemo(
    () => toComboboxOptions(classifiers?.previousVersions, { includeNone: true }),
    [classifiers?.previousVersions],
  );
  const subscriptionTypeComboboxOptions: ComboboxOption[] = useMemo(
    () =>
      toComboboxOptions(classifiers?.subscriptionLicenseTypes, {
        includeNone: true,
      }),
    [classifiers?.subscriptionLicenseTypes],
  );
  const nsbOptions: ComboboxOption[] = useMemo(() => {
    const nsbIds = new Set([
      ...(classifiers?.nsbs ?? [])
        .map((nsb) => nsb.nsbId?.trim())
        .filter((nsbId): nsbId is string => Boolean(nsbId)),
      ...values.allowedNsbIds.filter(Boolean),
    ]);

    return [...nsbIds].map((nsbId) => ({ value: nsbId, label: nsbId }));
  }, [classifiers?.nsbs, values.allowedNsbIds]);

  const selectedDocumentServiceOptions = getSelectedOptions(
    documentServiceComboboxOptions,
    values.documentServices,
  );
  const selectedLanguageOptions = getSelectedOptions(
    languageComboboxOptions,
    values.languages,
  );
  const selectedNsbOptions = nsbOptions.filter((option) =>
    values.allowedNsbIds.includes(option.value),
  );
  const allowMultipleNSBs =
    values.subscriptionType !== CustomCollectionSubscriptionType;

  const updateValue = <K extends FieldPath<ServiceLevelFormValues>>(
    key: K,
    value: FieldPathValue<ServiceLevelFormValues, K>,
  ) => {
    setValue(key, value, { shouldDirty: true, shouldValidate: true });
  };

  const handleSubscriptionTypeChange = (subscriptionType: number) => {
    updateValue('subscriptionType', subscriptionType);
    if (
      subscriptionType === CustomCollectionSubscriptionType &&
      values.allowedNsbIds.length > 1
    ) {
      updateValue('allowedNsbIds', values.allowedNsbIds.slice(0, 1));
    }
  };

  const handlePriceFactorModeChange = (
    isServiceLevelPriceFactorEnabled: boolean,
  ) => {
    updateValue(
      'isServiceLevelPriceFactorEnabled',
      isServiceLevelPriceFactorEnabled,
    );
    if (!isServiceLevelPriceFactorEnabled) {
      updateValue('priceFactor', undefined);
    }
  };

  const toCreateRequest = (
    data: ServiceLevelFormValues,
  ): CreateServiceLevelDefinitionRequest => ({
    serviceLevelName: data.serviceLevelName.trim(),
    documentServices: toBitEnumValue(
      data.documentServices,
    ) as unknown as DocumentService,
    languages: toBitEnumValue(data.languages) as unknown as DocumentLanguage,
    draftStandards: toNoYesEnum(data.draftStandards),
    redline: toNoYesEnum(data.redline),
    previousVersions: String(data.previousVersions) as PreviousVersion,
    multiLanguage: data.multiLanguage,
    subscriptionLicenseType:
      data.subscriptionType as unknown as AgreementLicenseType,
    allowedNsbIds: data.allowedNsbIds,
    priceFactor: data.isServiceLevelPriceFactorEnabled
      ? data.priceFactor
      : undefined,
  });

  const toUpdateRequest = (
    data: ServiceLevelFormValues,
  ): UpdateServiceLevelDefinitionRequestBody => {
    const request = toCreateRequest(data);
    return {
      serviceLevelName: request.serviceLevelName,
      documentServices: request.documentServices,
      languages: request.languages,
      draftStandards: request.draftStandards,
      redline: request.redline,
      previousVersions: request.previousVersions,
      multiLanguage: request.multiLanguage,
      subscriptionLicenseType: request.subscriptionLicenseType,
      updateExistingSubscriptions: data.updateExistingSubscriptions,
      priceFactor: request.priceFactor,
    };
  };

  const saveServiceLevel: SubmitHandler<ServiceLevelFormValues> = async (data) => {
    if (editingServiceLevelId === undefined) {
      await createServiceLevelDefinition({
        createServiceLevelDefinitionRequest: toCreateRequest(data),
      }).unwrap();
      toast.success(t('serviceLevels.successfullyCreated'));
    } else {
      await updateServiceLevelDefinition({
        recId: editingServiceLevelId,
        updateServiceLevelDefinitionRequestBody: toUpdateRequest(data),
      }).unwrap();
      await updateNsbSetups({
        serviceLevelDefinition: editingServiceLevelId,
        body: data.allowedNsbIds.map((nsbId) => ({
          recId: 0,
          serviceLevelDefinitionRefId: editingServiceLevelId,
          nsbId,
        })),
      }).unwrap();
      toast.success(t('serviceLevels.successfullyUpdated'));
    }
    reset(defaultServiceLevelFormValues);
    setIsConfirmationDialogOpen(false);
    onClose();
  };

  const submitServiceLevel = handleSubmit(async (data) => {
    if (isEditMode && data.updateExistingSubscriptions) {
      setIsConfirmationDialogOpen(true);
      return;
    }

    await saveServiceLevel(data);
  });

  const confirmUpdateExistingSubscriptions = handleSubmit(async (data) => {
    setIsConfirmationDialogOpen(false);
    await saveServiceLevel(data);
  });

  if (isFetchingClassifiers) {
    return <Spinner size="large" position="centerAbsolute" />;
  }

  return (
    <form
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        void submitServiceLevel();
      }}
      noValidate
    >
      <FormCard
        title={
          isEditMode
            ? t('serviceLevels.editServiceLevel')
            : t('serviceLevels.addServiceLevel')
        }
        onSave={() => {
          void submitServiceLevel();
        }}
        onCancel={onClose}
        disabled={isSaving}
        loading={isSaving}
        isEditMode={isEditMode}
        isDirty={isDirty}
        dirtyFieldsCount={Object.keys(dirtyFields).length}
      >
        <FormErrorSummary errors={errors} formRef={formRef} />
        <NameInput
          register={register}
          error={errors.serviceLevelName?.message}
          isDirty={Boolean(dirtyFields.serviceLevelName)}
          disabled={isSaving}
        />
        <ServicesSelect
          options={documentServiceComboboxOptions}
          selectedOptions={selectedDocumentServiceOptions}
          value={values.documentServices}
          onChange={(value) => updateValue('documentServices', value)}
          announcementTexts={announcementTexts}
          error={errors.documentServices?.message}
          isDirty={Boolean(dirtyFields.documentServices)}
          disabled={isSaving}
        />
        <LanguagesSelect
          options={languageComboboxOptions}
          selectedOptions={selectedLanguageOptions}
          value={values.languages}
          onChange={(value) => updateValue('languages', value)}
          announcementTexts={announcementTexts}
          error={errors.languages?.message}
          isDirty={Boolean(dirtyFields.languages)}
          disabled={isSaving}
        />
        <PreviousVersionsRadio
          options={previousVersionComboboxOptions}
          value={values.previousVersions}
          onChange={(value) => updateValue('previousVersions', value)}
          isDirty={Boolean(dirtyFields.previousVersions)}
          disabled={isSaving}
        />
        <SubscriptionTypeSelect
          options={subscriptionTypeComboboxOptions}
          value={values.subscriptionType}
          onChange={handleSubscriptionTypeChange}
          isDirty={Boolean(dirtyFields.subscriptionType)}
          disabled={isSaving}
        />
        <BooleanCheckboxes
          draftStandards={values.draftStandards}
          redline={values.redline}
          multiLanguage={values.multiLanguage}
          onDraftStandardsChange={(checked) =>
            updateValue('draftStandards', checked)
          }
          onRedlineChange={(checked) => updateValue('redline', checked)}
          onMultiLanguageChange={(checked) => updateValue('multiLanguage', checked)}
          isDirty={
            Boolean(dirtyFields.draftStandards) ||
            Boolean(dirtyFields.redline) ||
            Boolean(dirtyFields.multiLanguage)
          }
          disabled={isSaving}
        />
        <AllowedNsbsSelect
          options={nsbOptions}
          selectedOptions={selectedNsbOptions}
          value={values.allowedNsbIds}
          onChange={(value) => updateValue('allowedNsbIds', value)}
          allowMultiple={allowMultipleNSBs}
          announcementTexts={announcementTexts}
          error={errors.allowedNsbIds?.message}
          isDirty={Boolean(dirtyFields.allowedNsbIds)}
          disabled={isSaving}
        />
        <PriceFactorModeRadio
          value={values.isServiceLevelPriceFactorEnabled}
          onChange={handlePriceFactorModeChange}
          isDirty={Boolean(dirtyFields.isServiceLevelPriceFactorEnabled)}
          disabled={isSaving}
        />
        {values.isServiceLevelPriceFactorEnabled ? (
          <PriceFactorInput
            register={register}
            error={errors.priceFactor?.message}
            isDirty={Boolean(dirtyFields.priceFactor)}
            disabled={isSaving}
          />
        ) : null}
        {isEditMode ? (
          <UpdateExistingSubscriptionsRadio
            value={values.updateExistingSubscriptions}
            onChange={(value) => updateValue('updateExistingSubscriptions', value)}
            isDirty={Boolean(dirtyFields.updateExistingSubscriptions)}
            disabled={isSaving}
          />
        ) : null}
        {isEditMode ? (
          <ConfirmationDialog
            open={isConfirmationDialogOpen}
            onOpenChange={setIsConfirmationDialogOpen}
            onConfirm={confirmUpdateExistingSubscriptions}
          />
        ) : null}
      </FormCard>
    </form>
  );
}

export default ServiceLevelForm;
